/* eslint-disable import/no-cycle */
import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Field, reduxForm, formValueSelector, propTypes } from "redux-form"
import { defineMessages, injectIntl } from "react-intl"
import {
  Box,
  Divider,
  FormControlLabel,
  MenuItem,
  makeStyles,
  Radio
} from "@material-ui/core"
import FieldTextarea from "../../../component/forms/FieldTextarea"
import SelectInput from "../../../component/forms/SelectInput"
import { ButtonClear, ButtonSubmit } from "../../../component/buttons"
import FieldComboBox from "../../../component/forms/FieldComboBox"
import { buildAliases, findBundleKey, pages } from "../../../widgets/pages"
import { WIDGET_TYPES } from "../../../widgets/types"
import { WIDGET_SCOPES } from "../../../widgets/scopes"
import { words } from "../../../i18n/lstrings"
import useAppStyle from "../../../styles/appStyle"
import FieldWidgetsByVisitorProfile, {
  makeWidgetsByVisitorProfileRow
} from "../../../component/forms/FieldWidgetsByVisitorProfile"
import FieldRadioGroup from "../../../component/forms/FieldRadioGroup"
import { WIDGET_SEPARATION_MODES } from "../../../widgets/separationModes"
import { WIDGET_PERIODS } from "../../../widgets/period"

const useStyles = makeStyles(() => ({
  fieldsContainer: {
    "& > *": {
      width: "100%"
    }
  }
}))

const labels = defineMessages({
  type: {
    id: "widget.form.type",
    description: "widget type",
    defaultMessage: "Widget type"
  },
  scope: {
    id: "widget.form.scope",
    description: "widget scope",
    defaultMessage: "Scope"
  },
  dashboard: {
    id: "widget.form.dashboard",
    description: "widget dashboard",
    defaultMessage: "Dashboard"
  },
  defaultDashboard: {
    id: "widget.form.defaultDashboard",
    defaultMessage: "Default dashboard"
  },
  defaultDashboardDescription: {
    id: "widget.form.defaultDashboard.description",
    defaultMessage:
      "This dashboard is displayed to users without a visitor profile or with a visitor profile not referenced above."
  },
  defaultBookmark: {
    id: "widget.form.defaultBookmark",
    defaultMessage: "Default bookmark"
  },
  defaultBookmarkDescription: {
    id: "widget.form.defaultBookmark.description",
    defaultMessage:
      "This bookmark is displayed to users without a visitor profile or with a visitor profile not referenced above."
  },
  defaultSelector: {
    id: "widget.form.defaultSelector",
    defaultMessage: "Default selector"
  },
  defaultSelectorDescription: {
    id: "widget.form.defaultSelector.description",
    defaultMessage:
      "This selector is displayed to users without a visitor profile or with a visitor profile not referenced above."
  },
  sameWidgetForAll: {
    id: "widget.form.dashboard.separationMode.sameForAll",
    defaultMessage: "Display the same widget for everyone"
  },
  separateWidgetsByVisitorProfile: {
    id: "widget.form.dashboard.separationMode.byVisitorProfile",
    defaultMessage:
      "Display a different widget depending on the visitor's profile"
  },
  bookmark: {
    id: "widget.form.bookmark",
    description: "widget bookmark",
    defaultMessage: "Bookmark"
  },
  selectorName: {
    id: "widget.form.selectorName",
    description: "selector name",
    defaultMessage: "Selector name"
  },
  widgetAlias: {
    id: "widget.form.alias",
    defaultMessage: "Widget alias"
  },
  widgetPage: {
    id: "widget.form.widgetPage",
    defaultMessage: "Display page"
  },
  invalidJSON: {
    id: "widget.form.error.invalidJSON",
    defaultMessage: "Field is invalid. Expected a valid JSON for defaultParams"
  },
  period: {
    defaultMessage: "Default Period",
    description: "default period",
    id: "widget.form.period"
  }
})

let WidgetPropertiesForm = ({
  separationMode,
  handleSubmit,
  pristine,
  reset,
  submitting,
  handleSave,
  type,
  intl
}) => {
  const classes = useStyles()
  const { formatMessage: format } = intl
  const appStyleClasses = useAppStyle()
  const withDashboardField =
    type === WIDGET_TYPES.DASHBOARD ||
    type === WIDGET_TYPES.BOOKMARK ||
    type === WIDGET_TYPES.TEMPLATE

  const separatedByVisitorProfile =
    separationMode ===
      WIDGET_SEPARATION_MODES.SEPARATE_WIDGET_BY_VISITOR_PROFILE &&
    type !== WIDGET_TYPES.TEMPLATE

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      <Box
        className={classes.fieldsContainer}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Field
          component={SelectInput}
          label={format(labels.type)}
          name="widget"
        >
          {Object.entries(WIDGET_TYPES)
            .filter(([, it]) => it !== WIDGET_TYPES.SELECTOR) // FIXME: Temporarily disabled
            .map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {value.toUpperCase()}
              </MenuItem>
            ))}
        </Field>

        <Field
          component={SelectInput}
          label={format(labels.scope)}
          name="scope"
        >
          {Object.entries(WIDGET_SCOPES).map(([key, value]) => (
            <MenuItem key={key} value={value}>
              {value.toUpperCase()}
            </MenuItem>
          ))}
        </Field>

        <Field
          name="alias"
          type="text"
          component={FieldComboBox}
          label={format(labels.widgetAlias)}
          placeholder={format(labels.widgetAlias)}
          options={buildAliases()}
          autoComplete
          includeInputInList
        />

        <Field
          name="screen"
          type="text"
          component={SelectInput}
          label={format(labels.widgetPage)}
          placeholder={format(labels.widgetPage)}
          helpText
        >
          {pages.map((page) => {
            const bundleKey = findBundleKey(page)
            return (
              <MenuItem key={bundleKey.id} value={page}>
                {format(bundleKey)}
              </MenuItem>
            )
          })}
        </Field>
        {type !== WIDGET_TYPES.TEMPLATE && (
          <>
            <Box mt={1}>
              <Divider />
            </Box>

            <Field name="separationMode" component={FieldRadioGroup}>
              <FormControlLabel
                value={WIDGET_SEPARATION_MODES.SAME_WIDGET_FOR_ALL}
                control={<Radio />}
                label={format(labels.sameWidgetForAll)}
              />
              <FormControlLabel
                control={<Radio />}
                label={format(labels.separateWidgetsByVisitorProfile)}
                value={
                  WIDGET_SEPARATION_MODES.SEPARATE_WIDGET_BY_VISITOR_PROFILE
                }
              />
            </Field>
          </>
        )}

        {separatedByVisitorProfile && (
          <>
            <Box px={2}>
              <Field
                component={FieldWidgetsByVisitorProfile}
                name="widgetsByVisitorProfile"
                widgetType={type}
              />
            </Box>
            <Box py={2}>
              <Divider />
            </Box>
          </>
        )}

        {withDashboardField && (
          <Field
            fullWidth
            component={FieldTextarea}
            name="defaultDashboardUuid"
            type="text"
            label={format(
              separatedByVisitorProfile
                ? labels.defaultDashboard
                : labels.dashboard
            )}
            helperText={
              separatedByVisitorProfile &&
              format(labels.defaultDashboardDescription)
            }
          />
        )}

        {type === WIDGET_TYPES.BOOKMARK && (
          <Field
            component={FieldTextarea}
            name="defaultBookmarkName"
            type="text"
            label={format(
              separatedByVisitorProfile
                ? labels.defaultBookmark
                : labels.bookmark
            )}
            helperText={
              separatedByVisitorProfile &&
              format(labels.defaultBookmarkDescription)
            }
          />
        )}

        {type === WIDGET_TYPES.SELECTOR && (
          <Field
            component={FieldTextarea}
            name="name"
            type="text"
            label={format(
              separatedByVisitorProfile
                ? labels.defaultSelector
                : labels.selectorName
            )}
            helperText={
              separatedByVisitorProfile &&
              format(labels.defaultSelectorDescription)
            }
          />
        )}
        <Field
          component={SelectInput}
          label={format(labels.period)}
          name="advancedOptions.period"
        >
          {Object.entries(WIDGET_PERIODS).map(([key, value]) => (
            <MenuItem key={key} value={value}>
              {value.toUpperCase()}
            </MenuItem>
          ))}
        </Field>
      </Box>

      <Box className={appStyleClasses.formButton}>
        <ButtonClear disabled={pristine || submitting} onClick={reset} />
        <ButtonSubmit disabled={submitting} />
      </Box>
    </form>
  )
}

const validate = (values, props) => {
  const { formatMessage: format } = props.intl
  const errors = {}

  if (!values.widget) {
    errors.widget = format(words.required)
  }

  if (!values.alias) {
    errors.alias = format(words.required)
  }

  if (values.defaultParams) {
    try {
      // should be a valid JSON
      JSON.parse(values.defaultParams)
    } catch (err) {
      errors.defaultParams = format(labels.invalidJSON)
    }
  }

  switch (values.widget) {
    case WIDGET_TYPES.SELECTOR:
      if (!values.selectorName) {
        errors.selectorName = format(words.required)
      }
      break
    case WIDGET_TYPES.BOOKMARK:
      if (!values.defaultDashboardUuid) {
        errors.defaultDashboardUuid = format(words.required)
      }
      if (!values.defaultBookmarkName) {
        errors.defaultBookmarkName = format(words.required)
      }
      break
    case WIDGET_TYPES.TEMPLATE:
    case WIDGET_TYPES.DASHBOARD:
      if (!values.defaultDashboardUuid) {
        errors.defaultDashboardUuid = format(words.required)
      }
      break

    default:
      break
  }

  return errors
}

function handleOnFormChange(newValues, dispatch, props, previousValues) {
  const {
    widget: widgetType,
    separationMode,
    widgetsByVisitorProfile
  } = newValues
  const { widget: oldWidgetType } = previousValues
  const { change: changeField } = props

  // Template types cannot be separated
  if (
    widgetType === WIDGET_TYPES.TEMPLATE &&
    separationMode ===
      WIDGET_SEPARATION_MODES.SEPARATE_WIDGET_BY_VISITOR_PROFILE
  ) {
    changeField("separationMode", WIDGET_SEPARATION_MODES.SAME_WIDGET_FOR_ALL)
    return
  }

  if (separationMode === WIDGET_SEPARATION_MODES.SAME_WIDGET_FOR_ALL) {
    return
  }

  const dashboardListIsEmpty =
    !Array.isArray(widgetsByVisitorProfile) ||
    widgetsByVisitorProfile.length === 0

  if (dashboardListIsEmpty) {
    // Ensure there's always at least one row
    dispatch(
      changeField("widgetsByVisitorProfile", [
        makeWidgetsByVisitorProfileRow(widgetType)
      ])
    )
  }

  if (!dashboardListIsEmpty && widgetType !== oldWidgetType) {
    // Ensure each row object is transformed to the have the right columns for the current widget type
    dispatch(
      changeField(
        "widgetsByVisitorProfile",
        widgetsByVisitorProfile.map((row) =>
          makeWidgetsByVisitorProfileRow(widgetType, { ...row })
        )
      )
    )
  }
}

WidgetPropertiesForm.propTypes = {
  ...propTypes,
  dispatch: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired
}

WidgetPropertiesForm = injectIntl(
  reduxForm({
    form: "WidgetPropertiesForm",
    onChange: handleOnFormChange,
    validate
  })(WidgetPropertiesForm)
)

export default connect(
  (state, ownProps) => {
    const { initialValues } = ownProps
    const selector = formValueSelector("WidgetPropertiesForm")
    // Get State First, else get initial Values
    // Finally set a default values to Dashboard
    const type =
      selector(state, "widget") ??
      initialValues?.widget ??
      WIDGET_TYPES.DASHBOARD

    const separationMode =
      selector(state, "separationMode") ??
      initialValues?.separationMode ??
      WIDGET_SEPARATION_MODES.SAME_WIDGET_FOR_ALL

    return { type, separationMode }
  },
  (dispatch) => ({ dispatch })
)(WidgetPropertiesForm)
